<template>
  <div class="admin-login-container">
    <div class="admin-login-sky" aria-hidden="true"></div>
    <div class="admin-login-stars" aria-hidden="true"></div>
    <div class="admin-login-vignette" aria-hidden="true"></div>

    <div class="admin-login-wrap">
      <div class="admin-login-logo">
        <div class="logo-block"></div>
        <div class="logo-text">
          <div class="logo-title">云边小镇</div>
          <div class="logo-subtitle">ADMIN ACCESS</div>
        </div>
      </div>

      <div class="admin-login-card" role="region" aria-label="管理员登录">
        <div class="card-top">
          <div class="card-top-left">
            <div class="mc-badge">WORKBENCH</div>
            <h1 class="admin-login-title">管理员登录</h1>
          </div>
          <a class="card-top-right" href="/index">返回首页</a>
        </div>

        <div class="card-body">
          <div class="hint-line">请输入邮箱验证码</div>

          <div class="admin-login-row">
            <input
              v-model.trim="email"
              type="email"
              class="mc-input mc-input-email"
              placeholder="请输入邮箱"
              autocomplete="email"
              @keyup.enter="login"
            />
          </div>

          <div class="admin-login-row admin-login-row-inline">
            <input
              v-model.trim="code"
              type="text"
              class="mc-input"
              placeholder="请输入邮箱验证码"
              autocomplete="one-time-code"
              @keyup.enter="login"
            />
            <button class="mc-btn mc-btn-primary admin-send-btn" :disabled="sending || !email || countdown > 0" @click="sendCode">
              {{ sending ? '发送中...' : countdown > 0 ? `${countdown}s` : '发送邮箱验证码' }}
            </button>
          </div>

          <div class="admin-login-row">
            <button class="mc-btn mc-btn-primary mc-btn-login" :disabled="loggingIn || !email || !code" @click="login">
              {{ loggingIn ? '登录中...' : '登录' }}
            </button>
          </div>

          <div v-if="error" class="admin-login-error">{{ error }}</div>

          <div class="card-footer">
            <div class="footer-tip">验证码 5 分钟有效 · Token 7 天有效</div>
          </div>
        </div>

        <div class="card-corners" aria-hidden="true">
          <span class="corner tl"></span>
          <span class="corner tr"></span>
          <span class="corner bl"></span>
          <span class="corner br"></span>
        </div>
      </div>

      <div class="admin-login-ground" aria-hidden="true">
        <div class="ground-grass"></div>
        <div class="ground-dirt"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { API_BASE } from '../config/apiBase'

// API_BASE moved to @/config/apiBase


export default {
  name: 'AdminLogin',
  setup() {
    const router = useRouter()
    const route = useRoute()

    const email = ref('')
    const code = ref('')
    const error = ref('')

    const sending = ref(false)
    const loggingIn = ref(false)

    const countdown = ref(0)
    let timer = null

    const startCountdown = () => {
      countdown.value = 60
      if (timer) clearInterval(timer)
      timer = setInterval(() => {
        countdown.value -= 1
        if (countdown.value <= 0) {
          clearInterval(timer)
          timer = null
        }
      }, 1000)
    }

    const sendCode = async () => {
      error.value = ''
      if (!email.value) {
        error.value = '请输入邮箱'
        return
      }
      if (countdown.value > 0) return

      sending.value = true
      try {
        const res = await fetch(`${API_BASE}/admin-auth/sendEmailCode`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.value })
        })
        const data = await res.json()
        if (data && data.code === 200) {
          startCountdown()
        } else {
          error.value = (data && (data.message || data.msg)) || '发送失败'
        }
      } catch (e) {
        error.value = '发送失败'
      } finally {
        sending.value = false
      }
    }

    const login = async () => {
      error.value = ''
      if (!email.value || !code.value) {
        error.value = '请输入邮箱与验证码'
        return
      }

      loggingIn.value = true
      try {
        const res = await fetch(`${API_BASE}/admin-auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.value, code: code.value })
        })
        const data = await res.json()
        if (data && data.code === 200 && data.token) {
          localStorage.setItem('admin_token', data.token)
          localStorage.setItem('admin_token_exp', String(data.exp || ''))

          const redirect = route.query.redirect
          router.replace(typeof redirect === 'string' && redirect ? redirect : '/set')
        } else {
          error.value = (data && (data.message || data.msg)) || '登录失败'
        }
      } catch (e) {
        error.value = '登录失败'
      } finally {
        loggingIn.value = false
      }
    }

    onUnmounted(() => {
      if (timer) clearInterval(timer)
    })

    return {
      email,
      code,
      error,
      sending,
      loggingIn,
      countdown,
      sendCode,
      login
    }
  }
}
</script>

<style>
:root {
  --mc-green: #5ea818;
  --mc-bg: #0b0b0c;
  --mc-ink: #0b0b0c;
  --mc-panel: rgba(20, 20, 22, 0.92);
  --mc-border: #2b2b2e;
  --mc-border2: #3a3a40;
  --mc-stone: #7a7a7a;
  --mc-dirt: #6b4a2a;
  --mc-dirt-dark: #3b2716;
  --mc-grass: #2fa12d;
  --mc-grass-dark: #1f6f1e;
}

.admin-login-container {
  min-height: 100vh;
  background: var(--mc-bg);
  position: relative;
  overflow: hidden;
  padding: 36px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.admin-login-sky {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(1200px 700px at 30% 25%, rgba(94, 168, 24, 0.18), transparent 55%),
    radial-gradient(900px 520px at 70% 30%, rgba(255, 255, 255, 0.06), transparent 60%),
    linear-gradient(180deg, #070708 0%, #0b0b0c 40%, #060607 100%);
  filter: saturate(1.1);
}

.admin-login-stars {
  position: absolute;
  inset: -40px;
  background-image:
    radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px),
    radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px),
    radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px);
  background-size: 120px 120px, 180px 180px, 260px 260px;
  background-position: 0 0, 40px 60px, 90px 120px;
  opacity: 0.18;
  transform: translateZ(0);
  animation: starsDrift 18s linear infinite;
}

@keyframes starsDrift {
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(-60px, 80px, 0); }
}

.admin-login-vignette {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(60% 45% at 50% 40%, transparent 35%, rgba(0,0,0,0.75) 78%, rgba(0,0,0,0.92) 100%);
  pointer-events: none;
}

.admin-login-wrap {
  position: relative;
  width: min(860px, 96vw);
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: center;
}

.admin-login-logo {
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: center;
}

.logo-block {
  width: 44px;
  height: 44px;
  border: 4px solid #000;
  box-shadow: inset -3px -3px 0 rgba(0,0,0,0.45), inset 3px 3px 0 rgba(255,255,255,0.12);
  background:
    linear-gradient(135deg, rgba(47,161,45,1) 0%, rgba(31,111,30,1) 55%, rgba(15,60,15,1) 100%);
  position: relative;
}

.logo-block::after {
  content: '';
  position: absolute;
  inset: 8px;
  border: 3px solid rgba(0,0,0,0.5);
  background:
    repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 6px, rgba(0,0,0,0.06) 6px 12px);
}

.logo-text {
  text-align: left;
}

.logo-title {
  font-size: 26px;
  color: #fff;
  text-shadow: 5px 5px 0 #000;
  letter-spacing: 2px;
  font-weight: 800;
}

.logo-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255,255,255,0.68);
  letter-spacing: 3px;
}

.admin-login-card {
  position: relative;
  width: min(560px, 92vw);
  margin: 0 auto;
  background: var(--mc-panel);
  border: 4px solid var(--mc-border);
  box-shadow:
    0 24px 70px rgba(0,0,0,0.55),
    inset -6px -6px 0 rgba(0,0,0,0.4),
    inset 6px 6px 0 rgba(255,255,255,0.06);
  padding: 18px 18px 16px 18px;
  backdrop-filter: blur(2px);
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 3px solid rgba(94,168,24,0.28);
}

.card-top-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-top-right {
  font-size: 12px;
  text-decoration: none;
  color: #fff;
  padding: 10px 14px;
  border: 3px solid #fff;
  background: rgba(0, 0, 0, 0.45);
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.35), inset 2px 2px 0 rgba(255,255,255,0.1);
}

.card-top-right:hover {
  background: rgba(94,168,24,0.7);
  border-color: rgba(94,168,24,1);
}

.mc-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 4px 10px;
  font-size: 11px;
  letter-spacing: 2px;
  color: #0b0b0c;
  background: linear-gradient(135deg, #9ae054 0%, #5ea818 100%);
  border: 3px solid #000;
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.35), inset 2px 2px 0 rgba(255,255,255,0.14);
  font-weight: 800;
}

.admin-login-title {
  margin: 0;
  font-size: 22px;
  color: #fff;
  text-shadow: 4px 4px 0 #000;
}

.card-body {
  padding-top: 14px;
}

.hint-line {
  color: rgba(255,255,255,0.78);
  font-size: 12px;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.admin-login-row {
  margin-top: 12px;
}

.admin-login-row-inline {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: stretch;
}

.admin-send-btn {
  white-space: nowrap;
  min-width: 132px;
}

.mc-input {
  background: rgba(0,0,0,0.78);
  color: var(--mc-green);
  border: 3px solid var(--mc-stone);
  padding: 12px 12px;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.4;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  box-shadow: inset -2px -2px 0 rgba(0,0,0,0.35), inset 2px 2px 0 rgba(255,255,255,0.06);
}

.mc-input:focus {
  border-color: var(--mc-green);
  box-shadow: 0 0 0 3px rgba(94,168,24,0.16), inset -2px -2px 0 rgba(0,0,0,0.35), inset 2px 2px 0 rgba(255,255,255,0.06);
}

.mc-input::placeholder {
  color: rgba(255,255,255,0.35);
}

.mc-btn {
  padding: 12px;
  border: 3px solid #000;
  font-family: inherit;
  background: #999;
  box-shadow: inset -3px -3px 0 #555, inset 3px 3px 0 #ddd;
  cursor: pointer;
}

.mc-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mc-btn-primary {
  background: var(--mc-green);
  box-shadow: inset -3px -3px 0 #3c6b10, inset 3px 3px 0 #9ae054;
  color: white;
}

.mc-btn-login {
  width: 100%;
}

.admin-login-error {
  margin-top: 12px;
  color: #ff6b6b;
  font-size: 12px;
  text-align: center;
}

.card-footer {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 3px solid rgba(255,255,255,0.08);
}

.footer-tip {
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  letter-spacing: 1px;
  text-align: center;
}

.card-corners .corner {
  position: absolute;
  width: 14px;
  height: 14px;
  border: 4px solid #000;
  background: rgba(0,0,0,0.35);
}

.card-corners .tl { top: -4px; left: -4px; border-right: 0; border-bottom: 0; }
.card-corners .tr { top: -4px; right: -4px; border-left: 0; border-bottom: 0; }
.card-corners .bl { bottom: -4px; left: -4px; border-right: 0; border-top: 0; }
.card-corners .br { bottom: -4px; right: -4px; border-left: 0; border-top: 0; }

.admin-login-ground {
  width: min(760px, 96vw);
  margin: 0 auto;
  height: 92px;
  border: 4px solid #000;
  box-shadow: 0 18px 50px rgba(0,0,0,0.55);
  overflow: hidden;
}

.ground-grass {
  height: 34px;
  background:
    repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 10px, rgba(0,0,0,0.08) 10px 20px),
    linear-gradient(180deg, var(--mc-grass) 0%, var(--mc-grass-dark) 100%);
  border-bottom: 4px solid rgba(0,0,0,0.7);
}

.ground-dirt {
  height: calc(100% - 34px);
  background:
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.09) 0 18px, transparent 19px),
    radial-gradient(circle at 64% 52%, rgba(0,0,0,0.18) 0 22px, transparent 23px),
    repeating-linear-gradient(90deg, rgba(0,0,0,0.12) 0 12px, rgba(255,255,255,0.06) 12px 24px),
    linear-gradient(180deg, var(--mc-dirt) 0%, var(--mc-dirt-dark) 100%);
}

@media (max-width: 768px) {
  .admin-login-logo {
    flex-direction: column;
    text-align: center;
  }

  .logo-text {
    text-align: center;
  }

  .logo-title {
    font-size: 20px;
  }

  .card-top {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .card-top-right {
    width: 100%;
    text-align: center;
  }

  .admin-login-row-inline {
    grid-template-columns: 1fr;
  }

  .admin-send-btn {
    width: 100%;
  }
}
</style>

